import { Package, ArrowUpRight, TrendingDown, TrendingUp, Minus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const MaterialCard = ({ material, onClick }) => {
  const hasAvailability = Number.isFinite(material.available_quantity);
  const stockPercentage = hasAvailability && material.stock_quantity > 0
    ? (material.available_quantity / material.stock_quantity) * 100
    : null;
  
  const getStockLevel = () => {
    if (stockPercentage === null) return { level: "unknown", color: "", label: "Availability unavailable" };
    if (stockPercentage >= 70) return { level: "high", color: "stock-high", label: "Good Stock" };
    if (stockPercentage >= 30) return { level: "medium", color: "stock-medium", label: "Medium Stock" };
    return { level: "low", color: "stock-low", label: "Low Stock" };
  };
  
  const stockInfo = getStockLevel();
  
  const getTypeBadgeClass = () => {
    switch (material.material_type) {
      case "RAW": return "badge-raw";
      case "SPARE": return "badge-spare";
      case "CONS": return "badge-cons";
      default: return "bg-secondary text-secondary-foreground";
    }
  };
  
  const formatNumber = (num) => {
    if (!Number.isFinite(num)) return "—";
    return new Intl.NumberFormat('en-US', { 
      maximumFractionDigits: 0 
    }).format(num);
  };

  const StockTrendIcon = () => {
    if (stockPercentage === null) return <Minus className="w-3 h-3 text-slate-400" />;
    if (stockPercentage >= 70) return <TrendingUp className="w-3 h-3 text-emerald-500" />;
    if (stockPercentage >= 30) return <Minus className="w-3 h-3 text-amber-500" />;
    return <TrendingDown className="w-3 h-3 text-red-500" />;
  };
  
  return (
    <Card 
      className="card-industrial material-card cursor-pointer h-full"
      onClick={() => onClick?.(material)}
      data-testid={`material-card-${material.material_number}`}
    >
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-secondary flex items-center justify-center">
              <Package className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="data-value text-primary" data-testid="material-number">
                {material.material_number}
              </p>
              <span className={`badge-type ${getTypeBadgeClass()}`}>
                {material.material_type}
              </span>
            </div>
          </div>
          <ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        
        {/* Description */}
        <h3 className="font-medium text-foreground mb-4 line-clamp-2 min-h-[2.5rem]" data-testid="material-description">
          {material.description}
        </h3>
        
        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
          <div>
            <p className="text-muted-foreground text-xs uppercase tracking-wide">Base Unit</p>
            <p className="data-value">{material.base_unit}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs uppercase tracking-wide">Plant</p>
            <p className="data-value">{material.plant}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs uppercase tracking-wide">Group</p>
            <p className="data-value">{material.material_group}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs uppercase tracking-wide">Updated</p>
            <p className="data-value text-xs">
              {material.last_updated ? new Date(material.last_updated).toLocaleDateString() : "—"}
            </p>
          </div>
        </div>
        
        {/* Stock Section */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Stock Level</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1">
                    <StockTrendIcon />
                    <span className="text-xs font-medium">{stockInfo.label}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{stockPercentage === null ? "Available and reserved quantities require an additional SAP source" : `${stockPercentage.toFixed(1)}% available`}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          
          {/* Stock Bar */}
          <div className="stock-indicator mb-3">
            <div 
              className={`stock-indicator-fill ${stockInfo.color}`}
              style={{ width: `${stockPercentage ?? 0}%` }}
            />
          </div>
          
          {/* Stock Numbers */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="data-value text-lg" data-testid="stock-total">
                {formatNumber(material.stock_quantity)}
              </p>
              <p className="text-xs text-muted-foreground">Total</p>
            </div>
            <div>
              <p className="data-value text-lg text-emerald-600" data-testid="stock-available">
                {formatNumber(material.available_quantity)}
              </p>
              <p className="text-xs text-muted-foreground">Available</p>
            </div>
            <div>
              <p className="data-value text-lg text-amber-600" data-testid="stock-reserved">
                {formatNumber(material.reserved_quantity)}
              </p>
              <p className="text-xs text-muted-foreground">Reserved</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MaterialCard;
